import { useContext, useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { UserContext } from './UserContext';
import TradeCompleteModal from '../components/TradeCompleteModal';

// Only trades completed ON OR AFTER this date can trigger the modal.
// Trades completed before this feature launched are silently marked as
// already-celebrated so existing users never see the modal for old trades.
const FEATURE_LAUNCH_MS = new Date('2026-06-23T00:00:00Z').getTime();

/**
 * TradeCompleteProvider — mounts once at app level. Fires TradeCompleteModal
 * exactly once each time the user completes a NEW peer-to-peer trade.
 *
 * Key distinctions:
 *   - `completedTrades` on the user doc is incremented by BOTH peer trades
 *     AND cash purchases. We must verify by querying tradeOffers directly.
 *   - Trades completed before FEATURE_LAUNCH_MS are silently marked celebrated
 *     so no existing user sees the modal for old trades on first load.
 *   - We compare the most recent completed tradeOffer's completedAt timestamp
 *     against `users/{uid}.lastTradeCelebrationAt` to ensure we only celebrate
 *     genuinely new peer trades, not old ones resurfaced by a purchase bump.
 *
 * State-tracking:
 *   Primary:   users/{uid}.lastTradeCelebrationAt  (Firestore timestamp, cross-device)
 *   Secondary: localStorage `tradeComplete_celebrated_{uid}` set to the
 *              completedTrades count — prevents re-trigger while the write propagates.
 */
/**
 * Resolves once this page is in the foreground. A celebration that fires in a
 * hidden tab or a backgrounded app is "seen" by nobody: on 2026-10-13 the
 * owner's trade completed, a background session popped the modal 30s later
 * and marked it celebrated across devices, and opening the app afterwards
 * showed nothing. So the modal (and the celebrated mark) wait for visibility.
 */
export function whenVisible(doc_ = typeof document !== 'undefined' ? document : null) {
  if (!doc_ || doc_.visibilityState !== 'hidden') return Promise.resolve();
  return new Promise((resolve) => {
    const onChange = () => {
      if (doc_.visibilityState === 'hidden') return;
      doc_.removeEventListener('visibilitychange', onChange);
      resolve();
    };
    doc_.addEventListener('visibilitychange', onChange);
  });
}

const TradeCompleteProvider = ({ children }) => {
  const { firestoreUser } = useContext(UserContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [counterpartyName, setCounterpartyName] = useState(null);
  const [tradeId, setTradeId] = useState(null);
  // The completedTrades count already handled this session (not a boolean, so
  // a second completion in the same long-lived app session still celebrates).
  const handledRef = useRef(0);
  // Bumped to re-run the check after a failed lookup (bounded by retriesRef).
  const [retryNonce, setRetryNonce] = useState(0);
  const retriesRef = useRef(0);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid || !firestoreUser) return;

    // ── DEBUG BYPASS (localhost testing only) ─────────────────────────────────
    // In the browser console run: localStorage.setItem('tcm_debug', '1')
    // then reload. Clears itself after showing so it doesn't loop.
    if (localStorage.getItem('tcm_debug') === '1') {
      localStorage.removeItem('tcm_debug');
      const runDebug = async () => {
        let partner = null;
        try {
          const [fromSnap, toSnap] = await Promise.all([
            getDocs(query(collection(db, 'tradeOffers'), where('fromUserId', '==', uid), limit(50))),
            getDocs(query(collection(db, 'tradeOffers'), where('toUserId', '==', uid), limit(50))),
          ]);
          const all = [
            ...fromSnap.docs.map(d => ({ id: d.id, ...d.data() })),
            ...toSnap.docs.map(d => ({ id: d.id, ...d.data() })),
          ].filter(t => t.status === 'complete' || t.status === 'completed');
          all.sort((a, b) => (b.completedAt?.seconds || 0) - (a.completedAt?.seconds || 0));
          const trade = all[0];
          if (trade) {
            const isFrom = trade.fromUserId === uid;
            // Prefer the username snapshot baked onto the trade doc (same as TradeProgress.jsx)
            partner = isFrom
              ? trade.toUserSnapshot?.username || null
              : trade.fromUserSnapshot?.username || null;
            // Fallback to publicProfiles if snapshot is missing
            if (!partner) {
              const cpId = isFrom ? trade.toUserId : trade.fromUserId;
              const profileSnap = await getDoc(doc(db, 'publicProfiles', cpId));
              if (profileSnap.exists()) partner = profileSnap.data().username || null;
            }
          }
        } catch (e) {
          console.error('[TradeComplete debug] lookup failed:', e);
        }
        setCounterpartyName(partner);
        setIsModalOpen(true);
      };
      runDebug();
      return;
    }
    // ─────────────────────────────────────────────────────────────────────────

    const completedTrades = firestoreUser.completedTrades || 0;
    if (completedTrades === 0) return;

    // Fast-path: use localStorage to avoid re-triggering during write propagation
    const localKey = `tradeComplete_celebrated_${uid}`;
    const localCelebrated = parseInt(localStorage.getItem(localKey) || '0', 10);
    const firestoreCelebrated = firestoreUser.completedTradesAtLastCelebration || 0;
    const celebratedCount = Math.max(localCelebrated, firestoreCelebrated);

    if (completedTrades <= celebratedCount) return;

    // Guard against double-firing within the same session
    if (handledRef.current >= completedTrades) return;
    const previousHandled = handledRef.current;
    handledRef.current = completedTrades;

    // The localStorage mark is written only once the modal is actually on
    // screen (below). Claimed up front, it outlived a background session that
    // never became visible (app killed, hidden tab closed) and blocked that
    // device for good; handledRef already stops double-firing in-session.
    // A failed lookup (e.g. offline right after the app wakes) must not burn
    // the celebration either: release the claim so a retry can run.
    const releaseClaim = () => {
      handledRef.current = previousHandled;
    };

    const markCelebrated = async () => {
      try {
        await updateDoc(doc(db, 'users', uid), {
          completedTradesAtLastCelebration: completedTrades,
        });
      } catch (e) {
        console.error('[TradeComplete] Failed to persist celebration state:', e);
      }
    };

    const run = async () => {
      // Query both sides of tradeOffers — single-field equality, no composite index needed.
      // No limit: an unordered limit(50) is an arbitrary 50, and active traders
      // already have more offers than that per side (the owner: 55 and 73), so
      // the newest completed trade could be missed. This runs only when the
      // completed count goes up.
      const [fromSnap, toSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'tradeOffers'),
          where('fromUserId', '==', uid)
        )),
        getDocs(query(
          collection(db, 'tradeOffers'),
          where('toUserId', '==', uid)
        )),
      ]);

      const allTrades = [
        ...fromSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        ...toSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      ].filter(t => t.status === 'complete' || t.status === 'completed');

      // Sort newest first
      allTrades.sort((a, b) => {
        const aMs = (a.completedAt?.seconds || a.autoCompletedAt?.seconds || 0) * 1000;
        const bMs = (b.completedAt?.seconds || b.autoCompletedAt?.seconds || 0) * 1000;
        return bMs - aMs;
      });

      const recentTrade = allTrades[0];

      if (!recentTrade) {
        await markCelebrated();
        return;
      }

      const tradeSec = recentTrade.completedAt?.seconds
        || recentTrade.autoCompletedAt?.seconds
        || 0;
      const tradeMs = tradeSec * 1000;

      // A trade an admin force-closed is not something to throw confetti over —
      // it usually means a package was lost. Mark it celebrated so we skip it
      // permanently rather than re-checking and popping the modal later.
      if (recentTrade.completedBy === 'admin') {
        await markCelebrated();
        return;
      }

      // Trade predates this feature — silently mark so existing users never see
      // the modal for trades they completed before it was built
      if (tradeMs < FEATURE_LAUNCH_MS) {
        await markCelebrated();
        return;
      }

      // Trade already celebrated on another device or session
      const lastCelebSec = firestoreUser.lastTradeCelebrationAt?.seconds || 0;
      if (lastCelebSec > 0 && tradeSec <= lastCelebSec) {
        await markCelebrated();
        return;
      }

      // We have a genuinely new completed peer trade — fetch counterparty username.
      // Prefer the snapshot baked onto the trade doc (same as TradeProgress.jsx),
      // fall back to publicProfiles only if the snapshot is missing.
      let partner = null;
      try {
        const isFrom = recentTrade.fromUserId === uid;
        partner = isFrom
          ? recentTrade.toUserSnapshot?.username || null
          : recentTrade.fromUserSnapshot?.username || null;

        if (!partner) {
          const counterpartyId = isFrom ? recentTrade.toUserId : recentTrade.fromUserId;
          if (counterpartyId) {
            const profileSnap = await getDoc(doc(db, 'publicProfiles', counterpartyId));
            if (profileSnap.exists()) {
              partner = profileSnap.data().username || null;
            }
          }
        }
      } catch (e) {
        console.error('[TradeComplete] Failed to load counterparty:', e);
      }

      // Only celebrate where someone can see it (see whenVisible).
      await whenVisible();
      if (auth.currentUser?.uid !== uid) { releaseClaim(); return; }

      localStorage.setItem(localKey, String(completedTrades));
      setCounterpartyName(partner);
      setTradeId(recentTrade.id);
      setIsModalOpen(true);

      // Persist both the count and the timestamp so other devices sync correctly
      try {
        await updateDoc(doc(db, 'users', uid), {
          completedTradesAtLastCelebration: completedTrades,
          lastTradeCelebrationAt: serverTimestamp(),
        });
      } catch (e) {
        console.error('[TradeComplete] Failed to persist celebration state:', e);
      }
    };

    run().then(() => { retriesRef.current = 0; }).catch((e) => {
      console.error('[TradeComplete] check failed, will retry:', e);
      releaseClaim();
      if (retriesRef.current < 3) {
        retriesRef.current += 1;
        setTimeout(() => setRetryNonce((n) => n + 1), 15000);
      }
    });
  }, [
    !!firestoreUser,
    firestoreUser?.completedTrades,
    firestoreUser?.completedTradesAtLastCelebration,
    retryNonce,
  ]);

  const handleClose = () => setIsModalOpen(false);

  return (
    <>
      {children}
      <TradeCompleteModal
        isOpen={isModalOpen}
        counterpartyName={counterpartyName}
        tradeId={tradeId}
        onClose={handleClose}
      />
    </>
  );
};

export default TradeCompleteProvider;
